// src/types/tipologia.ts

/**
 * Tipologías macro de infraestructuras críticas PTEL.
 *
 * Cumple los criterios mínimos del Anexo 1 del Decreto 69/2024 (Directrices
 * para la elaboración de los PTEL) y el marco habilitante del Decreto 197/2024,
 * de 3 de septiembre, del Sistema Local de Protección Civil. Las categorías
 * concretas son una construcción técnica del proyecto basada en el análisis
 * empírico de los PTEL de los 785 municipios andaluces.
 *
 * Establecido como tipo canónico en sesión TUN2 (7-may-2026) tras DECISION
 * documentada en `.ptel/DECISION_TIPOLOGIA_UNIFY_20260507_TUN1.md`.
 *
 * Los valores string coinciden 1:1 con el enum legacy `InfrastructureType`
 * (`src/types/infrastructure.ts`) para preservar comparaciones textuales en
 * los 149 ficheros del visor mientras se completan TUN3 y TUN4.
 *
 * REFACTOR TUN4-B' (8-may-2026): convertido de `string enum` nominal a
 * `const … as const` + type alias derivado del mismo nombre. Razón
 * técnica: el enum nominal no permitía asignar string literals
 * (`'SANITARIO'`) a campos tipados `TipologiaPTEL`, lo que obligaba
 * a mantener el alias transitorio `TipologiaKey = TipologiaPTEL |
 * `${TipologiaPTEL}`` (eliminado en este mismo commit junto con
 * sus 29 referencias en 6 ficheros productivos + 1 test). El patrón
 * const-as-const acepta ambos: acceso miembro (`TipologiaPTEL.SANITARIO`,
 * 150 sitios) y asignación literal (`'SANITARIO'`, ~50 sitios).
 *
 * Compatibilidad verificada en auditoría empírica TUN4-B' §3:
 * - 150 accesos miembro `TipologiaPTEL.X` intactos (compat const-as).
 * - 2 `Object.values(TipologiaPTEL)` compatibles.
 * - 0 `keyof typeof` / `Object.keys` / `Object.entries` / reverse mapping.
 * - 0 imports type-only (todos los 16 ficheros importan runtime).
 *
 * @module types/tipologia
 */
export const TipologiaPTEL = {
  SANITARIO:    'SANITARIO',
  EDUCATIVO:    'EDUCATIVO',
  POLICIAL:     'POLICIAL',
  BOMBEROS:     'BOMBEROS',
  CULTURAL:     'CULTURAL',
  RELIGIOSO:    'RELIGIOSO',
  DEPORTIVO:    'DEPORTIVO',
  MUNICIPAL:    'MUNICIPAL',
  SOCIAL:       'SOCIAL',
  COMBUSTIBLE:  'COMBUSTIBLE',
  EMERGENCIAS:  'EMERGENCIAS',
  HIDRAULICO:   'HIDRAULICO',
  ENERGIA:      'ENERGIA',
  TELECOM:      'TELECOM',
  VIAL:         'VIAL',
  INDUSTRIAL:   'INDUSTRIAL',
  RSU:          'RSU',
  PUERTO:       'PUERTO',
  GENERICO:     'GENERICO',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare -- patrón canónico TS para const+type homónimos
export type TipologiaPTEL = typeof TipologiaPTEL[keyof typeof TipologiaPTEL];

/**
 * Array de las 19 categorías macro en orden canónico.
 * Útil para iterar sin depender de `Object.values()` del const.
 */
export const TIPOLOGIAS_VALUES: readonly TipologiaPTEL[] = Object.freeze(
  Object.values(TipologiaPTEL) as TipologiaPTEL[],
);
